import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from "recharts";

const Financials = ({
    isMobile,
    startDate,
    endDate
}) => {

    /* ---------------- STATE ---------------- */

    const [financialTotals, setFinancialTotals] = useState(null);
    const [dailyStats, setDailyStats] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);

    /* ---------------- API CALL ---------------- */

    useEffect(() => {
        const fetchFinancialTotals = async () => {
            const vendorId = localStorage.getItem("vendorId");
            const authToken = localStorage.getItem("authToken");

            setLoadingStats(true);

            try {
                const res = await axios.get(
                    "http://testing.maamaas.com:8080/food/api/orders/vendor/statistics/custom",
                    {
                        params: {
                            vendorId,
                            fromDate: startDate,
                            toDate: endDate
                        },
                        headers: {
                            Authorization: `Bearer ${authToken}`
                        }
                    }
                );

                const apiData = res.data || {};

                // ✅ SAVE DAILY STATS
                setDailyStats(apiData.dailyStats || []);

                const totalRevenue = apiData.totalRevenue ?? apiData.grossRevenue ?? 0;
                const netRevenue = apiData.netRevenue ?? 0;
                const expenses = totalRevenue - netRevenue;

                setFinancialTotals({
                    revenue: totalRevenue,
                    expenses,
                    profit: netRevenue,
                    margin:
                        parseFloat(apiData.profitMargin) ||
                        (totalRevenue > 0 ? (netRevenue / totalRevenue) * 100 : 0)
                });

            } catch (err) {
                console.error("❌ Financial API error:", err);
                setFinancialTotals({ revenue: 0, expenses: 0, profit: 0, margin: 0 });
                setDailyStats([]);
            } finally {
                setLoadingStats(false);
            }
        };

        fetchFinancialTotals();
    }, [startDate, endDate]);

    /* ---------------- LAST 12 MONTHS (FROM dailyStats) ---------------- */

    const monthlyRevenueData = useMemo(() => {
        const result = [];
        const now = new Date();

        // Create last 12 months buckets
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);

            result.push({
                key: `${d.getFullYear()}-${d.getMonth()}`,
                month: d.toLocaleString("en-IN", {
                    month: "short",
                    year: "numeric"
                }),
                Revenue: 0
            });
        }

        // Aggregate dailyStats into months
        dailyStats.forEach(item => {
            if (!item.date || !item.revenue) return;

            const d = new Date(item.date);
            const key = `${d.getFullYear()}-${d.getMonth()}`;

            const monthObj = result.find(m => m.key === key);
            if (monthObj) {
                monthObj.Revenue += Number(item.revenue);
            }
        });

        return result;
    }, [dailyStats]);

    /* ---------------- SUMMARY TOTALS ---------------- */

    const summary = useMemo(() => {
        if (!financialTotals) {
            return { revenue: 0, expenses: 0, profit: 0, margin: 0 };
        }

        const profit = financialTotals.revenue - financialTotals.expenses;
        const margin =
            financialTotals.revenue > 0
                ? (profit / financialTotals.revenue) * 100
                : 0;

        return {
            revenue: financialTotals.revenue,
            expenses: financialTotals.expenses,
            profit,
            margin
        };
    }, [financialTotals]);

    /* ---------------- SUMMARY CARDS ---------------- */

    const SummaryCards = () => {
        if (loadingStats) {
            return (
                <div style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                    Loading financial summary...
                </div>
            );
        }

        const cards = [
            {
                title: "Total Revenue",
                value: `₹${summary.revenue.toLocaleString("en-IN")}`,
                bg: "linear-gradient(135deg, #dcfce7, #bbf7d0)"
            },
            // {
            //     title: "Total Expenses",
            //     value: `₹${summary.expenses.toLocaleString("en-IN")}`,
            //     bg: "linear-gradient(135deg, #fee2e2, #fecaca)"
            // },
            {
                title: "Net Profit",
                value: `₹${summary.profit.toLocaleString("en-IN")}`,
                bg: "linear-gradient(135deg, #dbeafe, #bfdbfe)"
            },
            {
                title: "Profit Margin",
                value: `${summary.margin.toFixed(1)}%`,
                bg: "linear-gradient(135deg, #ede9fe, #ddd6fe)"
            }
        ];

        return (
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
                    gap: "12px",
                    marginBottom: "20px"
                }}
            >
                {cards.map((c, i) => (
                    <div
                        key={i}
                        style={{
                            background: c.bg,
                            padding: isMobile ? "12px" : "16px",
                            borderRadius: "12px",
                            border: "1px solid #fff"
                        }}
                    >
                        <p style={{ fontSize: 11, fontWeight: 600 }}>{c.title}</p>
                        <p style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800 }}>
                            {c.value}
                        </p>
                    </div>
                ))}
            </div>
        );
    };

    /* ---------------- CHART ---------------- */

    const RevenueChart = () => (
        <div
            style={{
                background: "white",
                borderRadius: "24px",
                padding: isMobile ? "20px" : "28px",
                border: "1px solid #e2e8f0"
            }}
        >
            <h4 style={{ fontWeight: 800, marginBottom: 16 }}>
                Revenue (Last 12 Months)
            </h4>

            <div style={{ height: isMobile ? 280 : 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyRevenueData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `₹${v}`} />
                        <Tooltip formatter={(v) => `₹${v.toLocaleString("en-IN")}`} />

                        <Bar
                            dataKey="Revenue"
                            fill="#10b981"
                            radius={[8, 8, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    /* ---------------- RENDER ---------------- */

    return (
        <>
            <SummaryCards />
            <RevenueChart />
        </>
    );
};

export default Financials;
